<template>
  <span class="x-area">
    <slot :value="convertedValue" :label="label">
      <span :class="{ locked: obscureValue }">{{ formattedValue + ' ' }}</span>
      <span v-if="labelType !== 'none'" class="x-area-unit">
        {{ label }}
      </span>
    </slot>
  </span>
</template>
<script setup lang="ts">
///// imports /////
import { computed } from 'vue'

import { getConversionFunction, formatNumber } from '@/lib/area'

import {
  AreaAbbrevs,
  AreaFullNames,
  AreaSingularFullName,
  type SupportedAreaTypes,
} from '@/lib/area/types'

///// props/emits /////
interface Props {
  area: number
  from: SupportedAreaTypes
  to: SupportedAreaTypes
  maximumFractionDigits?: number
  labelType?: 'none' | 'abbreviation' | 'full'
  obscureValue?: boolean
  shouldApproximateLessThanOne?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  area: undefined,
  from: 'sqMeters',
  to: 'acres',
  maximumFractionDigits: 0,
  labelType: 'abbreviation',
  obscureValue: false,
  shouldApproximateLessThanOne: false,
})

///// computed /////
const convertedValue = computed(() => {
  if (props.area === undefined) {
    return 0
  }

  const value = Number(props.area.toString().replace(/,/g, ''))

  // Check if conversion is needed
  if (props.to === props.from) {
    return value
  }

  // Do conversion based on props
  const convertTo = getConversionFunction(props.from)
  return convertTo(props.to)(value)
})

const formattedValue = computed(() => {
  const format = formatNumber(props.maximumFractionDigits)
  if (props.area === undefined) {
    return '--'
  }

  if (props.shouldApproximateLessThanOne && convertedValue.value < 1) {
    return 'Less than 1'
  }

  return format(convertedValue.value)
})

const label = computed(() => {
  const labelOptions = {
    abbreviation: AreaAbbrevs[props.to],
    full: AreaFullNames[props.to],
    none: '',
  }

  if (
    props.labelType === 'full' &&
    props.shouldApproximateLessThanOne &&
    (convertedValue.value < 1 || formattedValue.value === '1')
  ) {
    return AreaSingularFullName[props.to]
  }

  return labelOptions[props.labelType]
})
</script>
<style lang="scss" scoped>
.locked {
  filter: blur(3px);
  pointer-events: none;
}
</style>
